/**
 * 帶入選項（questionMappings 目標題的 slot）的穩定身分 [9EZ-608]。
 *
 * - 來源題「其他／以上皆無」的原始 op 索引另存一份，不放在 choiceIndexMapRef 裡：
 *   choiceIndexMapRef[來源].other/.none 會在來源題 onValueChanged（order reduce）與
 *   refreshMultipleChoiceOrder 被改寫成顯示位置，那是給 ans 用的語意，不能動。
 *   這裡以 choiceIndexMapRef 物件本身為 key（useRef 跨 render 是同一個物件），
 *   由 useConvertSurvey 在 pre-populate 時寫入，帶入邏輯讀它當 origin_op。
 * - 帶入的特殊選項用 kind（'other' | 'none'）辨識，與一般選項互不配對。
 *   續填時 slot 由 bringing_options 重建（只有 na、origin_op、origin_qi），
 *   舊資料特殊 slot 的 origin_op 可能是漂移後的顯示位置，所以只能由 na 推回 kind，
 *   且直接來源題要開了對應的特殊選項才算（見 inferBringInKind）。
 *   slot 沒有整數 origin_qi 時由 bringOptionsMapRef 反查來源題（findBringInSourceName）；
 *   仍取不到又像特殊選項 → 身分不明（identityUnknown），對位時一律對不到。
 */

// 帶入目標題中「其他」slot 的固定文字（useSubmitAnswers 帶入時寫入 na）
export const BRING_IN_OTHER_TEXT = '其他(上題填寫內容)'

const specialOrigins = new WeakMap()

export function recordSpecialOrigin(
  choiceIndexMapRef,
  questionName,
  kind,
  index
) {
  if (!choiceIndexMapRef || typeof choiceIndexMapRef !== 'object') return
  if (!specialOrigins.has(choiceIndexMapRef)) {
    specialOrigins.set(choiceIndexMapRef, {})
  }
  const store = specialOrigins.get(choiceIndexMapRef)
  store[questionName] = { ...store[questionName], [kind]: index }
}

// 回傳來源題特殊選項的原始 op 索引；沒有登記（未經 useConvertSurvey）回傳 undefined
export function getSpecialOrigin(choiceIndexMapRef, questionName, kind) {
  if (!choiceIndexMapRef || typeof choiceIndexMapRef !== 'object')
    return undefined
  return specialOrigins.get(choiceIndexMapRef)?.[questionName]?.[kind]
}

// 由 bringOptionsMapRef.current（{ 來源題名: 目標題名 }）反查目標題的來源題名；
// 反查不到回傳 undefined。slot 沒有可用的 origin_qi 時（9EZ-538 前的暫存）的後備，
// 暫存組裝（useSurveyAnswerConverter）與續填重建（useLoadProgress）共用這一份。
export function findBringInSourceName(bringOptionsMap, targetName) {
  return Object.keys(bringOptionsMap || {}).find(
    (name) => bringOptionsMap[name] === targetName
  )
}

// 找不到直接來源題時，slot 文字像特殊選項就無法判斷種類：
// 「其他」是固定文字；「以上皆無」看問卷內任一開了 showNoneItem 的題目 noneText。
// 這種 slot 不可當一般選項比 origin_op（舊資料特殊 slot 的 origin_op 可能已漂移，
// 會撞上一般選項），續填時標成身分不明、清空並記刪除事件。
export function isAmbiguousSpecialText(title, questions) {
  if (title === BRING_IN_OTHER_TEXT) return true
  return (questions || []).some(
    (q) => q && q.showNoneItem && q.noneText && q.noneText === title
  )
}

// 由 slot 文字推回特殊選項種類；一般選項回傳 undefined。
// sourceQuestion 是 slot 的直接來源題（q{origin_qi}，origin_qi 須先正規化成整數）。
// 條件與作答中途 useSubmitAnswers 寫 kind 時相同：來源題的值含 'other'／'none'
// 才會帶入特殊選項，而這兩個值只在來源題開了 showOtherItem／showNoneItem 時存在。
// 所以來源題沒開時，文字再像也是一般選項（例：連鎖帶入 q0→q1→q2，q1 帶入的
// 「其他(上題填寫內容)」對 q2 是 q1 的一般選項；或來源題一般選項文字剛好相同）。
// 找不到來源題時無從判斷，回傳 undefined；呼叫端要再用 isAmbiguousSpecialText
// 擋下長得像特殊選項的 slot（見 useLoadProgress 續填重建）。
export function inferBringInKind(title, sourceQuestion) {
  if (!sourceQuestion) return undefined
  if (sourceQuestion.showOtherItem && title === BRING_IN_OTHER_TEXT) {
    return 'other'
  }
  if (
    sourceQuestion.showNoneItem &&
    sourceQuestion.noneText &&
    title === sourceQuestion.noneText
  ) {
    return 'none'
  }
  return undefined
}
